import { type MessageInitShape, create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import {
  type ContractorListItem,
  type CustomerSupplyPoint,
  PrivateGenerationSchema,
  ContractStage as ProtoContractStage,
  type CustomerSummary as ProtoCustomerSummary,
  type PrivateGeneration as ProtoPrivateGeneration,
  SupplyPointDeleteBlockedReason,
  SupplyPointMasterCheckKind,
  SupplyPointMasterMismatch,
} from '@ebalance/proto/bffuser/v1/customer_model_pb'
import {
  type ConvertProspectToContractRequestSchema,
  type CreateCustomerSupplyPointRequestSchema,
  type GetCustomerSupplyPointDeletabilityResponse,
  type GetCustomerSupplyPointFormOptionsResponse,
  type UpdateCustomerSupplyPointRequestSchema,
} from '@ebalance/proto/bffuser/v1/customer_service_pb'
import { VoltageClass as ProtoVoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import {
  DateSchema,
  type Date as ProtoDate,
} from '@ebalance/proto/google/type/date_pb'
import {
  type Decimal,
  DecimalSchema,
} from '@ebalance/proto/google/type/decimal_pb'

import type { CivilDate } from '~/models/civilDate'
import { decimalToNumberOr } from '~/models/decimal'
import type { VoltageClass } from '~/models/voltageBreakdown'
import { isNumericKw } from '~/pages/CustomerInfo/models/kwInput'
import { SupplyPointError } from '~/pages/CustomerInfo/models/supplyPointError'
import type {
  ContractStage,
  ContractorRow,
  ConvertProspectInput,
  CreateSupplyPointInput,
  CustomerSummary,
  Deletability,
  DeletabilityBlockedReason,
  FormOptions,
  MasterCheck,
  PrivateGeneration,
  SupplyPoint,
  UpdateSupplyPointInput,
} from '~/pages/CustomerInfo/models/types'

/**
 * proto (CustomerService / ContractorService) と画面の型 (`./types.ts`) の変換。
 * 画面は proto を直接読まない。 数値は表示・入力の都合で number、 日付は暦日 (`CivilDate`) に落とす。
 */

// ---- 値の変換 ----------------------------------------------------------

const toCivilDate = (date: ProtoDate | undefined): CivilDate => ({
  year: date?.year ?? 0,
  month: date?.month ?? 0,
  day: date?.day ?? 0,
})

const toOptionalCivilDate = (
  date: ProtoDate | undefined,
): CivilDate | undefined => (date === undefined ? undefined : toCivilDate(date))

const toProtoDate = (date: CivilDate): ProtoDate =>
  create(DateSchema, { year: date.year, month: date.month, day: date.day })

/** 元の Decimal 文字列をそのまま送る (number を経由すると桁が丸まり、 小さい値は指数表記になる)。 */
const toProtoDecimal = (value: string): Decimal =>
  create(DecimalSchema, { value })

export const toVoltageClass = (voltage: ProtoVoltageClass): VoltageClass => {
  switch (voltage) {
    case ProtoVoltageClass.EXTRA_HIGH:
      return 'extraHigh'
    case ProtoVoltageClass.HIGH:
      return 'high'
    case ProtoVoltageClass.LOW:
      return 'low'
    case ProtoVoltageClass.UNSPECIFIED:
      throw new Error('voltage class is unspecified')
  }
}

export const toProtoVoltageClass = (
  voltage: VoltageClass,
): ProtoVoltageClass => {
  switch (voltage) {
    case 'extraHigh':
      return ProtoVoltageClass.EXTRA_HIGH
    case 'high':
      return ProtoVoltageClass.HIGH
    case 'low':
      return ProtoVoltageClass.LOW
  }
}

const toContractStage = (stage: ProtoContractStage): ContractStage => {
  switch (stage) {
    case ProtoContractStage.PROSPECT:
      return 'prospect'
    case ProtoContractStage.CONTRACTED:
      return 'contracted'
    case ProtoContractStage.LOST:
      return 'lost'
    case ProtoContractStage.UNSPECIFIED:
      throw new Error('contract stage is unspecified')
  }
}

const toProtoContractStage = (
  stage: CreateSupplyPointInput['stage'],
): ProtoContractStage =>
  stage === 'prospect'
    ? ProtoContractStage.PROSPECT
    : ProtoContractStage.CONTRACTED

const toPrivateGeneration = (
  generation: ProtoPrivateGeneration | undefined,
): PrivateGeneration => {
  if (generation === undefined || !generation.has) return { has: false }
  return {
    has: true,
    capacityKw: generation.capacityKw?.value,
    reversePowerFlowContractKw: generation.reversePowerFlowContractKw?.value,
  }
}

const toMasterCheck = (
  check: CustomerSupplyPoint['masterCheck'],
): MasterCheck => {
  switch (check?.kind) {
    case SupplyPointMasterCheckKind.NOT_IN_MASTER:
      return { kind: 'notInMaster' }
    case SupplyPointMasterCheckKind.MISMATCH:
      return {
        kind: 'mismatch',
        mismatches: check.mismatches.flatMap((m) => {
          switch (m) {
            case SupplyPointMasterMismatch.AREA:
              return ['area' as const]
            case SupplyPointMasterMismatch.VOLTAGE:
              return ['voltage' as const]
            case SupplyPointMasterMismatch.RETAILER:
              return ['retailer' as const]
            case SupplyPointMasterMismatch.UNSPECIFIED:
              return []
          }
        }),
      }
    case SupplyPointMasterCheckKind.MATCH:
    case SupplyPointMasterCheckKind.UNSPECIFIED:
    case undefined:
      return { kind: 'match' }
  }
}

// ---- 読み取り -----------------------------------------------------------

const requireTimestamp = (
  timestamp: CustomerSupplyPoint['updatedAt'],
): NonNullable<CustomerSupplyPoint['updatedAt']> => {
  if (timestamp === undefined) throw new Error('timestamp is missing')
  return timestamp
}

export const toSupplyPoint = (p: CustomerSupplyPoint): SupplyPoint => ({
  id: p.customerSupplyPointId,
  customerId: p.customerId,
  customerName: p.customerName,
  stage: toContractStage(p.contractStage),
  supplyPointNumber: p.supplyPointSpecificNumber,
  areaCode: p.areaCode,
  voltageClass: toVoltageClass(p.voltageClass),
  retailerBusinessCode: p.retailerBusinessCode,
  retailerName: p.retailerName,
  contractKw: p.contractKw?.value ?? '0',
  kwPeriods: p.kwPeriods.map((k) => ({
    start: toCivilDate(k.startDate),
    kw: k.contractKw?.value ?? '0',
  })),
  supplyStartDate: toCivilDate(p.supplyStartDate),
  supplyEndDate: toOptionalCivilDate(p.supplyEndDate),
  privateGeneration: toPrivateGeneration(p.privateGeneration),
  lostAt: p.lostAt === undefined ? undefined : timestampDate(p.lostAt),
  lostBy: p.lostBy,
  masterCheck: toMasterCheck(p.masterCheck),
  createdAt: timestampDate(requireTimestamp(p.createdAt)),
  createdBy: p.createdBy,
  updatedAt: timestampDate(requireTimestamp(p.updatedAt)),
  version: requireTimestamp(p.updatedAt),
  updatedBy: p.updatedBy,
})

export const toCustomerSummary = (
  c: ProtoCustomerSummary,
): CustomerSummary => ({
  customerId: c.customerId,
  customerName: c.customerName,
  contractorName: c.contractorName,
  contractorId: c.contractorId,
  areaCodes: c.areaCodes,
  voltageClasses: c.voltageClasses.map(toVoltageClass),
  contractedPointCount: c.contractedPointCount,
  prospectPointCount: c.prospectPointCount,
  contractedKwTotal: decimalToNumberOr(c.contractedKwTotal),
  prospectKwTotal: decimalToNumberOr(c.prospectKwTotal),
  createdAt: timestampDate(requireTimestamp(c.createdAt)),
  createdBy: c.createdBy,
  updatedAt: timestampDate(requireTimestamp(c.updatedAt)),
  updatedBy: c.updatedBy,
  version: requireTimestamp(c.updatedAt),
})

export const toFormOptions = (
  r: GetCustomerSupplyPointFormOptionsResponse,
): FormOptions => ({
  retailerOptions: r.retailerOptions.map((o) => ({
    businessCode: o.businessCode,
    businessName: o.businessName,
  })),
  customerOptions: r.customerOptions.map((o) => ({
    customerId: o.customerId,
    customerName: o.customerName,
    areaCodes: o.areaCodes,
    pointCount: o.pointCount,
    supplyPointNumber: o.supplyPointNumber,
  })),
  contractorOptions: r.contractorOptions.map((o) => ({
    contractorId: o.contractorId,
    contractorName: o.contractorName,
    customerCount: o.customerCount,
  })),
})

const toBlockedReason = (
  reason: SupplyPointDeleteBlockedReason | undefined,
): DeletabilityBlockedReason => {
  switch (reason) {
    case SupplyPointDeleteBlockedReason.LOST:
      return 'lost'
    case SupplyPointDeleteBlockedReason.IN_SUPPLY_POINT_MASTER:
      return 'inSupplyPointMaster'
    case SupplyPointDeleteBlockedReason.HAS_ACTUALS:
      return 'hasActuals'
    case SupplyPointDeleteBlockedReason.UNSPECIFIED:
    case undefined:
      throw new Error('deletability is blocked without a reason')
  }
}

export const toDeletability = (
  r: GetCustomerSupplyPointDeletabilityResponse,
): Deletability =>
  r.deletable
    ? { deletable: true }
    : { deletable: false, blockedReason: toBlockedReason(r.blockedReason) }

export const toContractorRow = (item: ContractorListItem): ContractorRow => {
  const c = item.contractor
  if (c === undefined) throw new Error('contractor is missing')
  return {
    contractor: {
      id: c.contractorId,
      name: c.name,
      billingPostalCode: c.billingPostalCode,
      billingAddress: c.billingAddress,
      billingAttention: c.billingAttention,
      note: c.note,
      createdAt: timestampDate(requireTimestamp(c.createdAt)),
      createdBy: c.createdBy,
      updatedAt: timestampDate(requireTimestamp(c.updatedAt)),
      version: requireTimestamp(c.updatedAt),
      updatedBy: c.updatedBy,
    },
    customerCount: item.customerCount,
    supplyPointCount: item.supplyPointCount,
    customerNames: item.customerNames.join('、'),
    deleteBlockedCustomerCount: item.customerCount,
  }
}

// ---- 書き込み -----------------------------------------------------------

/** 入力の kW。 空・数値でない値は unset (server が「0 より大きい値」の拒否理由を返す)。 */
const toOptionalKw = (kw: string | undefined): Decimal | undefined =>
  kw === undefined || !isNumericKw(kw) ? undefined : toProtoDecimal(kw)

const toProtoPrivateGeneration = (
  generation: PrivateGeneration,
): ProtoPrivateGeneration => {
  if (!generation.has) return create(PrivateGenerationSchema, { has: false })
  // 逆調契約の欄に数値でない文字がある時は「範囲外」として送らずに止める (空にして保存されるのを防ぐ)
  if (
    generation.reversePowerFlowContractKw !== undefined &&
    !isNumericKw(generation.reversePowerFlowContractKw)
  ) {
    throw new SupplyPointError('REVERSE_POWER_FLOW_OUT_OF_RANGE')
  }
  return create(PrivateGenerationSchema, {
    has: true,
    capacityKw: toOptionalKw(generation.capacityKw),
    reversePowerFlowContractKw: toOptionalKw(
      generation.reversePowerFlowContractKw,
    ),
  })
}

export const toCreateRequest = (
  input: CreateSupplyPointInput,
): MessageInitShape<typeof CreateCustomerSupplyPointRequestSchema> => {
  const customer = input.customer
  if (customer.kind === 'existing' && customer.customerId === undefined) {
    throw new SupplyPointError('CUSTOMER_REQUIRED')
  }
  if (
    customer.kind === 'new' &&
    input.contractor.kind === 'existing' &&
    input.contractor.contractorId === undefined
  ) {
    throw new SupplyPointError('CONTRACTOR_REQUIRED')
  }
  if (
    customer.kind === 'new' &&
    input.contractor.kind === 'new' &&
    input.contractor.name.trim() === ''
  ) {
    throw new SupplyPointError('CONTRACTOR_REQUIRED')
  }
  return {
    contractStage: toProtoContractStage(input.stage),
    customer:
      customer.kind === 'existing'
        ? {
            case: 'existingCustomerId',
            value: customer.customerId ?? '',
          }
        : { case: 'newCustomerName', value: customer.name },
    // 契約者は新しい需要家の時だけ送る: 既存を選んだら Id / 新しく作るなら契約者名
    contractorId:
      customer.kind === 'new' && input.contractor.kind === 'existing'
        ? input.contractor.contractorId
        : undefined,
    newContractorName:
      customer.kind === 'new' && input.contractor.kind === 'new'
        ? input.contractor.name.trim()
        : undefined,
    areaCode: input.areaCode,
    retailerBusinessCode: input.retailerBusinessCode,
    voltageClass: toProtoVoltageClass(input.voltageClass),
    supplyPointSpecificNumber: input.supplyPointNumber,
    contractKw: toOptionalKw(input.contractKw),
    supplyStartDate: toProtoDate(input.supplyStartDate),
    supplyEndDate:
      input.supplyEndDate === undefined
        ? undefined
        : toProtoDate(input.supplyEndDate),
    privateGeneration: toProtoPrivateGeneration(input.privateGeneration),
  }
}

export const toUpdateRequest = (
  input: UpdateSupplyPointInput,
): MessageInitShape<typeof UpdateCustomerSupplyPointRequestSchema> => ({
  customerSupplyPointId: input.supplyPointId,
  contractKw: toOptionalKw(input.contractKw),
  supplyStartDate: toProtoDate(input.supplyStartDate),
  supplyEndDate:
    input.supplyEndDate === undefined
      ? undefined
      : toProtoDate(input.supplyEndDate),
  // prospectNumber: undefined = 番号を変えない / '' = 消す / 値 = 入れる・直す
  prospectNumber:
    input.prospectNumber === undefined
      ? undefined
      : { supplyPointSpecificNumber: input.prospectNumber },
  kwPeriods:
    input.kwPeriods === undefined
      ? undefined
      : {
          periods: input.kwPeriods.map((k) => ({
            startDate: toProtoDate(k.start),
            contractKw: toProtoDecimal(k.kw),
          })),
        },
  areaCode: input.areaCode,
  voltageClass:
    input.voltageClass === undefined
      ? undefined
      : toProtoVoltageClass(input.voltageClass),
  supplyPointSpecificNumber: input.supplyPointNumber,
  retailerBusinessCode: input.retailerBusinessCode,
  expectedUpdatedAt: input.expectedUpdatedAt,
})

export const toConvertRequest = (
  input: ConvertProspectInput,
): MessageInitShape<typeof ConvertProspectToContractRequestSchema> => ({
  customerSupplyPointId: input.supplyPointId,
  supplyPointSpecificNumber: input.supplyPointNumber,
  retailerBusinessCode: input.retailerBusinessCode,
  contractKw: toOptionalKw(input.contractKw),
  supplyStartDate: toProtoDate(input.supplyStartDate),
  supplyEndDate:
    input.supplyEndDate === undefined
      ? undefined
      : toProtoDate(input.supplyEndDate),
  kwPeriods:
    input.kwPeriods === undefined
      ? undefined
      : {
          periods: input.kwPeriods.map((kw) => ({
            startDate: toProtoDate(kw.start),
            contractKw: toProtoDecimal(kw.kw),
          })),
        },
  expectedUpdatedAt: input.expectedUpdatedAt,
})
